<script setup lang="ts">
import { requestAccessAsNewCustomer, type RequestAccessAsNewCustomerRequest } from '@/api/profiles'
import { useMutation } from '@pinia/colada'
import { email, minLength, pipe, string } from 'valibot'
import { useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
definePage({
  meta: {
    layout: 'public',
    requiresAuth: false,
  },
})
const { t } = useI18n()
const { handleSubmit } = useForm<RequestAccessAsNewCustomerRequest>({
  initialValues: {
    companyName: '',
    country: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  },
})
const { mutateAsync, status } = useMutation({
  mutation: requestAccessAsNewCustomer,
})
const submitForm = handleSubmit(async data => {
  await mutateAsync(data)
})
</script>

<template>
  <div class="m-auto w-full rounded-lg bg-white shadow-sm sm:max-w-md md:mt-0 xl:p-0">
    <div class="space-y-4 p-6 sm:p-8 md:space-y-6">
      <h1 class="text-xl leading-tight font-bold tracking-tight text-gray-900 md:text-2xl">
        {{ t('newUser.newCustomer.title') }}
      </h1>
      <form
        class="space-y-4 md:space-y-6"
        @submit.prevent="submitForm()"
      >
        <InputField
          id="companyName"
          name="companyName"
          type="text"
          :placeholder="t('newUser.newCustomer.fields.companyName')"
          :label="t('newUser.newCustomer.fields.companyName')"
          :validator="pipe(string(), minLength(1, t('newUser.newCustomer.errorMessages.required')))"
        />
        <InputField
          id="country"
          name="country"
          type="text"
          :placeholder="t('newUser.newCustomer.fields.country')"
          :label="t('newUser.newCustomer.fields.country')"
          :validator="pipe(string(), minLength(1, t('newUser.newCustomer.errorMessages.required')))"
        />
        <InputField
          id="firstName"
          name="firstName"
          type="text"
          :placeholder="t('newUser.newCustomer.fields.firstName')"
          :label="t('newUser.newCustomer.fields.firstName')"
          :validator="pipe(string(), minLength(1, t('newUser.newCustomer.errorMessages.required')))"
        />
        <InputField
          id="lastName"
          name="lastName"
          type="text"
          :placeholder="t('newUser.newCustomer.fields.lastName')"
          :label="t('newUser.newCustomer.fields.lastName')"
          :validator="pipe(string(), minLength(1, t('newUser.newCustomer.errorMessages.required')))"
        />
        <InputField
          id="phoneNumber"
          name="phoneNumber"
          type="text"
          :placeholder="t('newUser.newCustomer.fields.phoneNo')"
          :label="t('newUser.newCustomer.fields.phoneNo')"
          :validator="pipe(string(), minLength(1, t('newUser.newCustomer.errorMessages.required')))"
        />
        <InputField
          id="email"
          name="email"
          type="email"
          :placeholder="t('newUser.newCustomer.fields.email')"
          :label="t('newUser.newCustomer.fields.email')"
          :validator="
            pipe(
              string(),
              minLength(1, t('newUser.newCustomer.errorMessages.required')),
              email(t('newUser.newCustomer.errorMessages.email')),
            )
          "
        />
        <Button
          color="green"
          type="submit"
        >
          {{ t('newUser.signIn') }}
        </Button>
        <p
          v-if="status === 'success'"
          class="text-sm font-light text-gray-500"
        >
          {{ t('newUser.newCustomer.successMsg') }}
        </p>
        <p
          v-else-if="status === 'error'"
          class="text-sm font-light text-gray-500"
        >
          {{ t('newUser.newCustomer.errorMsg') }}
        </p>
      </form>
    </div>
  </div>
</template>
